import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { showToast } from "../../lib/toast";
import {
  CreateSupplierDto,
  supplierService,
  UpdateSupplierDto,
} from "../../services/supplier.service";

const inputClass =
  "w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500";

const initialSupplier: CreateSupplierDto = {
  name: "",
  phone: "",
  email: "",
  code: "",
  contactPerson: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
  gstNumber: "",
  panNumber: "",
  drugLicenseNo: "",
  drugLicenseExpiry: "",
  supplierType: "DISTRIBUTOR",
  deliveryLeadDays: 0,
  minimumOrderValue: 0,
  rating: 0,
  returnPolicy: "",
  creditDays: 30,
  creditLimit: 0,
  bankName: "",
  accountNumber: "",
  ifscCode: "",
  tdsApplicable: false,
  tdsPercent: 0,
  tdsSection: "",
  isActive: true,
};

const possiblyMaskedFields = [
  "phone",
  "email",
  "address",
  "gstNumber",
  "panNumber",
  "accountNumber",
  "ifscCode",
] as const satisfies readonly (keyof CreateSupplierDto)[];

function apiError(error: any, fallback: string) {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join(". ");
  if (typeof message === "string") return message;
  const errors = error?.response?.data?.errors;
  if (Array.isArray(errors)) {
    return errors
      .map((item) => item?.message ?? String(item))
      .filter(Boolean)
      .join(". ");
  }
  return fallback;
}

export function CreateSupplierPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreateSupplierDto>(initialSupplier);
  const [maskedFields, setMaskedFields] = useState<
    Set<keyof CreateSupplierDto>
  >(new Set());
  const keepsMaskedPhone = isEditMode && maskedFields.has("phone");

  useEffect(() => {
    if (!isEditMode) return;
    void fetchSupplier();
  }, [id]);

  const fetchSupplier = async () => {
    try {
      setLoading(true);
      const supplier = await supplierService.getSupplier(id!);
      const hidden = new Set<keyof CreateSupplierDto>();
      for (const field of possiblyMaskedFields) {
        const value = supplier[field];
        if (typeof value === "string" && value.includes("*")) {
          hidden.add(field);
        }
      }
      setMaskedFields(hidden);
      const visible = (field: keyof CreateSupplierDto, value?: string) =>
        hidden.has(field) ? "" : (value ?? "");
      setFormData({
        name: supplier.name,
        phone: visible("phone", supplier.phone),
        email: visible("email", supplier.email),
        code: supplier.code ?? "",
        contactPerson: supplier.contactPerson ?? "",
        address: visible("address", supplier.address),
        city: supplier.city ?? "",
        state: supplier.state ?? "",
        pincode: supplier.pincode ?? "",
        gstNumber: visible("gstNumber", supplier.gstNumber),
        panNumber: visible("panNumber", supplier.panNumber),
        drugLicenseNo: supplier.drugLicenseNo ?? "",
        drugLicenseExpiry: supplier.drugLicenseExpiry?.slice(0, 10) ?? "",
        supplierType: supplier.supplierType ?? "DISTRIBUTOR",
        deliveryLeadDays: supplier.deliveryLeadDays ?? 0,
        minimumOrderValue: Number(supplier.minimumOrderValue ?? 0),
        rating: Number(supplier.rating ?? 0),
        returnPolicy: supplier.returnPolicy ?? "",
        creditDays: supplier.creditDays,
        creditLimit: Number(supplier.creditLimit),
        bankName: supplier.bankName ?? "",
        accountNumber: visible("accountNumber", supplier.accountNumber),
        ifscCode: visible("ifscCode", supplier.ifscCode),
        tdsApplicable: supplier.tdsApplicable ?? false,
        tdsPercent: Number(supplier.tdsPercent ?? 0),
        tdsSection: supplier.tdsSection ?? "",
        isActive: supplier.isActive,
      });
    } catch (error) {
      showToast.error(apiError(error, "Failed to fetch supplier details"));
      navigate("/suppliers");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value, type } = event.target;
    setFormData((current) => ({
      ...current,
      [name]:
        type === "number"
          ? value === ""
            ? undefined
            : Number(value)
          : type === "checkbox"
            ? (event.target as HTMLInputElement).checked
            : value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (
      !formData.name.trim() ||
      (!formData.phone.trim() && !keepsMaskedPhone)
    ) {
      showToast.error("Supplier name and phone are required");
      return;
    }
    if (formData.tdsApplicable && Number(formData.tdsPercent ?? 0) <= 0) {
      showToast.error("Enter a TDS percentage when TDS is applicable");
      return;
    }

    try {
      setLoading(true);
      if (isEditMode) {
        const updatePayload: UpdateSupplierDto = { ...formData };
        for (const field of maskedFields) delete updatePayload[field];
        await supplierService.updateSupplier(id!, updatePayload);
        showToast.success("Supplier updated successfully");
      } else {
        await supplierService.createSupplier(formData);
        showToast.success("Supplier created successfully");
      }
      navigate("/suppliers");
    } catch (error) {
      showToast.error(
        apiError(
          error,
          `Failed to ${isEditMode ? "update" : "create"} supplier`,
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <button
          type="button"
          onClick={() => navigate("/suppliers")}
          className="text-blue-600 hover:text-blue-800 mb-2"
        >
          ← Back to Suppliers
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? "Edit Supplier" : "Add New Supplier"}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Required fields are marked with an asterisk.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <section className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Basic Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <label className="text-sm font-medium text-gray-700">
              Supplier Name <span className="text-red-500">*</span>
              <input
                className={`${inputClass} mt-1`}
                name="name"
                value={formData.name}
                onChange={handleChange}
                maxLength={255}
                required
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Supplier Code
              <input
                className={`${inputClass} mt-1 uppercase`}
                name="code"
                value={formData.code}
                onChange={handleChange}
                maxLength={50}
                placeholder="Optional unique code"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Supplier Type
              <select
                className={`${inputClass} mt-1`}
                name="supplierType"
                value={formData.supplierType}
                onChange={handleChange}
              >
                <option value="DISTRIBUTOR">Distributor</option>
                <option value="MANUFACTURER">Manufacturer</option>
                <option value="WHOLESALER">Wholesaler</option>
                <option value="IMPORTER">Importer</option>
                <option value="STOCKIST">Stockist</option>
              </select>
            </label>
            <label className="text-sm font-medium text-gray-700">
              Phone <span className="text-red-500">*</span>
              <input
                className={`${inputClass} mt-1`}
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                pattern="\+?[0-9\s()\-]{8,20}"
                title="Enter 8 to 15 digits; spaces, brackets and hyphens are allowed"
                required={!keepsMaskedPhone}
                placeholder={
                  keepsMaskedPhone
                    ? "Leave blank to keep saved phone"
                    : undefined
                }
              />
              {keepsMaskedPhone && (
                <span className="mt-1 block text-xs text-gray-500">
                  The saved phone is protected. Enter a new number only to
                  replace it.
                </span>
              )}
            </label>
            <label className="text-sm font-medium text-gray-700">
              Email
              <input
                className={`${inputClass} mt-1`}
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Contact Person
              <input
                className={`${inputClass} mt-1`}
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleChange}
                maxLength={100}
              />
            </label>
          </div>
        </section>

        <section className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Address</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="text-sm font-medium text-gray-700 md:col-span-3">
              Address
              <textarea
                className={`${inputClass} mt-1`}
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows={2}
                maxLength={500}
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              City
              <input
                className={`${inputClass} mt-1`}
                name="city"
                value={formData.city}
                onChange={handleChange}
                maxLength={100}
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              State
              <input
                className={`${inputClass} mt-1`}
                name="state"
                value={formData.state}
                onChange={handleChange}
                maxLength={100}
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Pincode
              <input
                className={`${inputClass} mt-1`}
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                inputMode="numeric"
                pattern="[0-9]{6}"
                title="Enter a 6-digit pincode"
              />
            </label>
          </div>
        </section>

        <section className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Tax &amp; Compliance
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <label className="text-sm font-medium text-gray-700">
              GST Number
              <input
                className={`${inputClass} mt-1 uppercase`}
                name="gstNumber"
                value={formData.gstNumber}
                onChange={handleChange}
                pattern="[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z][1-9A-Za-z]Z[0-9A-Za-z]"
                title="Enter a valid 15-character GSTIN"
                placeholder="27ABCDE1234F1Z5"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              PAN Number
              <input
                className={`${inputClass} mt-1 uppercase`}
                name="panNumber"
                value={formData.panNumber}
                onChange={handleChange}
                pattern="[A-Za-z]{5}[0-9]{4}[A-Za-z]"
                title="Enter a valid 10-character PAN"
                placeholder="ABCDE1234F"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Drug License No.
              <input
                className={`${inputClass} mt-1 uppercase`}
                name="drugLicenseNo"
                value={formData.drugLicenseNo}
                onChange={handleChange}
                maxLength={50}
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              License Expiry
              <input
                className={`${inputClass} mt-1`}
                type="date"
                name="drugLicenseExpiry"
                value={formData.drugLicenseExpiry}
                onChange={handleChange}
              />
            </label>
          </div>
        </section>

        <section className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Commercial Terms
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <label className="text-sm font-medium text-gray-700">
              Credit Days
              <input
                className={`${inputClass} mt-1`}
                type="number"
                name="creditDays"
                value={formData.creditDays}
                onChange={handleChange}
                min={0}
                max={3650}
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Credit Limit (₹)
              <input
                className={`${inputClass} mt-1`}
                type="number"
                name="creditLimit"
                value={formData.creditLimit}
                onChange={handleChange}
                min={0}
                step="0.01"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Delivery Lead Days
              <input
                className={`${inputClass} mt-1`}
                type="number"
                name="deliveryLeadDays"
                value={formData.deliveryLeadDays}
                onChange={handleChange}
                min={0}
                max={3650}
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Minimum Order Value (₹)
              <input
                className={`${inputClass} mt-1`}
                type="number"
                name="minimumOrderValue"
                value={formData.minimumOrderValue}
                onChange={handleChange}
                min={0}
                step="0.01"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Rating (0–5)
              <input
                className={`${inputClass} mt-1`}
                type="number"
                name="rating"
                value={formData.rating}
                onChange={handleChange}
                min={0}
                max={5}
                step="0.1"
              />
            </label>
            <label className="text-sm font-medium text-gray-700 md:col-span-2 lg:col-span-3">
              Return Policy
              <textarea
                className={`${inputClass} mt-1`}
                name="returnPolicy"
                value={formData.returnPolicy}
                onChange={handleChange}
                rows={2}
                maxLength={1000}
              />
            </label>
          </div>
        </section>

        <section className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Bank &amp; TDS Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <label className="text-sm font-medium text-gray-700">
              Bank Name
              <input
                className={`${inputClass} mt-1`}
                name="bankName"
                value={formData.bankName}
                onChange={handleChange}
                maxLength={100}
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Account Number
              <input
                className={`${inputClass} mt-1`}
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleChange}
                maxLength={30}
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              IFSC Code
              <input
                className={`${inputClass} mt-1 uppercase`}
                name="ifscCode"
                value={formData.ifscCode}
                onChange={handleChange}
                pattern="[A-Za-z]{4}0[A-Za-z0-9]{6}"
                title="Enter a valid 11-character IFSC code"
              />
            </label>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                name="tdsApplicable"
                checked={formData.tdsApplicable}
                onChange={handleChange}
              />
              TDS applicable
            </label>
            <label className="text-sm font-medium text-gray-700">
              TDS Percent
              <input
                className={`${inputClass} mt-1`}
                type="number"
                name="tdsPercent"
                value={formData.tdsPercent}
                onChange={handleChange}
                min={0}
                max={100}
                step="0.01"
                disabled={!formData.tdsApplicable}
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              TDS Section
              <input
                className={`${inputClass} mt-1 uppercase`}
                name="tdsSection"
                value={formData.tdsSection}
                onChange={handleChange}
                maxLength={20}
                disabled={!formData.tdsApplicable}
                placeholder="e.g. 194C"
              />
            </label>
          </div>
        </section>

        {/* Approval reason UI is retained in SupplierApprovalsPage for a
            possible future deployment, but intentionally disabled here. */}

        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate("/suppliers")}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update Supplier"
                : "Create Supplier"}
          </button>
        </div>
      </form>
    </div>
  );
}
